import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWRImmutable from 'swr/immutable';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import {
  ContextTypes,
  Loading,
  TwoFactorAuthenticationMethodVerificationStepsOverrideStates,
  TwoFactorAuthenticationSetup,
  TwoFactorAuthenticationSetupProps,
} from '@tradingblock/components';
import Config from '../../../config';
import { useApi } from '../../../context/Api';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { GetTwoFactorAuthenticationDetailsUrl } from '@tradingblock/api';
import { PhoneModel, SiteGrp, TwoFactorAuthenticationStates } from '@tradingblock/types';
import { TwoFactorAuthenticationActions, UIActions } from '../../../data/global/actions';
import { AccountActions } from '../../../data/global/actions/AccountActions';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import { formatPhoneNumber } from '../../../utilities/accountManagement';

const StyledTwoFactorAuthenticationSection = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  /* Respond to the Account Management block width, not just the viewport */
  container-type: inline-size;
  container-name: two-fa;
  padding: 0.85rem 1rem 1rem;
  background: linear-gradient(165deg, rgba(0, 18, 54, 0.9) 0%, rgba(0, 30, 75, 0.5) 100%);
  border: 1px solid var(--lighten);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22), 0 1px 0 rgba(255, 255, 255, 0.06) inset;

  .header-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .header-wrapper h5,
  .method-header-wrapper h6 {
    margin-bottom: 0;
  }

  .main-content-wrapper {
    margin-top: 0.25rem;
  }

  .main-content-wrapper .verified {
    color: var(--teal);
    margin-right: 0.25rem;
  }

  .main-content-wrapper .verified i {
    margin-right: 0.25rem;
  }

  .method-header-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .method-header-wrapper h6 {
    margin-right: 0.25rem;
    line-height: 1.2;
    font-size: 0.95rem;
  }

  .two-fa-methods-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
  }

  /* Side-by-side methods when the *block* is wide enough (not only the window) */
  @container two-fa (min-width: 40rem) {
    .two-fa-methods-row {
      flex-direction: row;
      align-items: stretch;
      gap: 1rem;
    }

    .two-fa-col {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .two-fa-col--sms,
    .two-fa-col--email {
      flex: 1 1 0;
    }
  }

  @supports not (container-type: inline-size) {
    @media (min-width: 992px) {
      .two-fa-methods-row {
        flex-direction: row;
        align-items: stretch;
        gap: 1rem;
      }

      .two-fa-col {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      .two-fa-col--sms,
      .two-fa-col--email {
        flex: 1 1 0;
      }
    }
  }

  .two-fa-method-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 0.7rem 0.85rem;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 4px;
  }

  @container two-fa (min-width: 40rem) {
    .two-fa-method-panel {
      flex: 1 1 auto;
      min-height: 0;
    }
  }

  @supports not (container-type: inline-size) {
    @media (min-width: 992px) {
      .two-fa-method-panel {
        flex: 1 1 auto;
        min-height: 0;
      }
    }
  }

  .email-method-section,
  .sms-method-section {
    width: 100%;
  }

  .email-method-section p,
  .email-method-section .email-change-action {
    max-width: 100%;
  }

  .email-change-note {
    margin-bottom: 0.35rem;
    line-height: 1.35;
    max-width: 100%;
  }

  .email-change-action {
    display: inline-block;
    padding: 0;
    vertical-align: baseline;
    font-size: inherit;
    line-height: 1.35;
  }

  .email-change-action--after-email {
    margin-top: 0.5rem;
    margin-bottom: 0;
  }

  .contact-change-btn:hover, .update-email-btn:hover, .select-diff-phone-btn:hover, .send-new-code-btn:hover {
    color: white;
  }

  .sms-method-section {
    margin-bottom: 1rem;
  }

  .two-fa-methods-row .sms-method-section {
    margin-bottom: 0;
  }

  .sms-method-section p {
    margin-bottom: 0;
  }
`;

const TwoFactorAuthenticationSection = () => {
  const api = useApi();

  const dispatch = useDispatch();

  const twoFactorAuthenticationDetails = useStateSelector(s => s.twoFactorAuthentication.details);
  const twoFactorAuthenticationState = useStateSelector(s => s.account.profile.current && s.account.profile.current.twoFactorAuthenticationState);
  const primaryAccountHolderEmail = useStateSelector(s => s.accountManagement.accountManagementDetails.details.accountHolders[0].email);
  const primaryAccountHolderPhones = useStateSelector(s => s.accountManagement.accountManagementDetails.details.accountHolders[0].phones);
  const accountId = useStateSelector(s => s.account.accountId);
  const isLoadingAccountManagementDetails = useStateSelector(s => s.accountManagement.accountManagementDetails.isFetching);
  const errorGettingAccountManagementDetails = useStateSelector(s => s.accountManagement.accountManagementDetails.error);
  const isLoadingUserProfile = useStateSelector(s => s.account.profile.isFetching);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [smsTwoFactorAuthenticationMethodVerifiedPhoneNumber, setSmsTwoFactorAuthenticationMethodVerifiedPhoneNumber] = useState<string | null>(null);
  const [customTwoFactorAuthenticationSetupComponentProps, setCustomTwoFactorAuthenticationSetupComponentProps] = useState<Partial<TwoFactorAuthenticationSetupProps> | {}>({});
  const [showTwoFactorAuthenticationSetupComponent, setShowTwoFactorAuthenticationSetupComponent] = useState<boolean>(false);

  const prevTwoFactorAuthenticationState = useRef<TwoFactorAuthenticationStates | null | undefined>(twoFactorAuthenticationState);

  const genericErrorMsg = 'There was an issue on our end. Try moving to a different tab within the account management block and try again. If the issue persists, please contact Customer Service.';

  const siteGrp = Config.siteGroup as SiteGrp;

  const uuidForGetTwoFactorAuthenticationDetailsKey = useMemo(() => uuidV4(), []);

  const {
    isLoading: isLoadingTwoFactorAuthenticationDetails,
    data: getTwoFactoAuthenticationDetailsResponse,
    error: errorGettingTwoFactorAuthenticationDetails,
    isValidating: isValidatingTwoFactorAuthenticationDetails,
    mutate: getTwoFactorAuthenticationDetails,
  } = useSWRImmutable(
    `${Config.tradingApi}/${GetTwoFactorAuthenticationDetailsUrl}${uuidForGetTwoFactorAuthenticationDetailsKey}`,
    api.twoFactorAuthentication.getDetails,
    { revalidateOnFocus: false, shouldRetryOnError: false, revalidateOnMount: true }
  );

  useEffect(() => {
    dispatch(AccountActions.requestUserProfile());
  }, []);

  useEffect(() => {
    if (prevTwoFactorAuthenticationState.current && twoFactorAuthenticationState !== prevTwoFactorAuthenticationState.current) {
      getTwoFactorAuthenticationDetails();
    }
  }, [twoFactorAuthenticationState]);

  useEffect(() => {
    if (!twoFactorAuthenticationState) {
      setErrorMsg(genericErrorMsg);
    }
  }, [twoFactorAuthenticationState]);

  useEffect(() => {
    prevTwoFactorAuthenticationState.current = twoFactorAuthenticationState;
  }, [twoFactorAuthenticationState]);

  useEffect(() => {
    if (errorGettingTwoFactorAuthenticationDetails || errorGettingAccountManagementDetails) {
      setErrorMsg(genericErrorMsg);
    } else {
      setErrorMsg(null);
    }
  }, [errorGettingTwoFactorAuthenticationDetails, errorGettingAccountManagementDetails]);

  useEffect(() => {
    if (
      getTwoFactoAuthenticationDetailsResponse &&
      getTwoFactoAuthenticationDetailsResponse.responseCode === 0 &&
      getTwoFactoAuthenticationDetailsResponse.payload
    ) {
      const { smsVerifiedPhoneType, isEmailVerified, voiceVerifiedPhoneType } = getTwoFactoAuthenticationDetailsResponse.payload;

      dispatch(TwoFactorAuthenticationActions.receiveDetails({ smsVerifiedPhoneType, isEmailVerified, voiceVerifiedPhoneType }));

      const phoneNumberDataVerifiedAsSmsTwoFactorAuthenticationMethod = (primaryAccountHolderPhones.find((primaryAccountHolderPhone) => getTwoFactoAuthenticationDetailsResponse.payload.smsVerifiedPhoneType === primaryAccountHolderPhone.type)) as PhoneModel;

      if (phoneNumberDataVerifiedAsSmsTwoFactorAuthenticationMethod) {
        setSmsTwoFactorAuthenticationMethodVerifiedPhoneNumber(phoneNumberDataVerifiedAsSmsTwoFactorAuthenticationMethod.phoneNumber);
      }
    } else if (
      getTwoFactoAuthenticationDetailsResponse &&
      (
        getTwoFactoAuthenticationDetailsResponse.responseCode !== 0 ||
        !getTwoFactoAuthenticationDetailsResponse.payload
      )
    ) {
      setErrorMsg(genericErrorMsg);
    }
  }, [getTwoFactoAuthenticationDetailsResponse]);

  useEffect(() => {
    if (
      !isValidatingTwoFactorAuthenticationDetails &&
      !isLoadingTwoFactorAuthenticationDetails &&
      !isLoadingAccountManagementDetails &&
      !isLoadingUserProfile &&
      !errorMsg &&
      (
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndEmailMethodNotVerified ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndNoVerifiedMethods ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndSmsMethodNotVerified ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndEmailMethodNotVerified ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndNoVerifiedMethods ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndSmsMethodNotVerified
      )
    ) {
      setShowTwoFactorAuthenticationSetupComponent(true);
    } else {
      setShowTwoFactorAuthenticationSetupComponent(false);
    }
  }, [
    twoFactorAuthenticationState,
    isLoadingTwoFactorAuthenticationDetails,
    isValidatingTwoFactorAuthenticationDetails,
    errorMsg,
    isLoadingAccountManagementDetails,
    isLoadingUserProfile,
  ]);

  const handleVerifyEmailTwoFactorAuthenticationMethodBtnClick = () => {
    setShowTwoFactorAuthenticationSetupComponent(true);

    setCustomTwoFactorAuthenticationSetupComponentProps({
      twoFactorAuthenticationMethodVerificationStepsOverrideState: TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedEmailTwoFactorAuthenticationMethodVerification,
      allowAbilityToCancel: true,
      handleCancel: () => {
        getTwoFactorAuthenticationDetails();
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
        dispatch(AccountActions.requestUserProfile());
        setShowTwoFactorAuthenticationSetupComponent(false);
        setCustomTwoFactorAuthenticationSetupComponentProps({});
      },
    });
  };

  const handleVerifySmsTwoFactorAuthenticationMethodBtnClick = () => {
    setShowTwoFactorAuthenticationSetupComponent(true);

    setCustomTwoFactorAuthenticationSetupComponentProps({
      twoFactorAuthenticationMethodVerificationStepsOverrideState: TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedSmsTwoFactorAuthenticationMethodVerification,
      allowAbilityToCancel: true,
      handleCancel: () => {
        getTwoFactorAuthenticationDetails();
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
        dispatch(AccountActions.requestUserProfile());
        setShowTwoFactorAuthenticationSetupComponent(false);
        setCustomTwoFactorAuthenticationSetupComponentProps({});
      },
    });
  };

  const handleUpdateSmsTwoFactorAuthenticationMethodBtnClick = () => {
    setShowTwoFactorAuthenticationSetupComponent(true);

    const smsTwoFactorAuthenticationMethodVerifiedPhoneData = (primaryAccountHolderPhones
    .find((primaryAccountHolderPhone) =>
      primaryAccountHolderPhone.type === (twoFactorAuthenticationDetails && twoFactorAuthenticationDetails.smsVerifiedPhoneType)
    ) as PhoneModel);

    setCustomTwoFactorAuthenticationSetupComponentProps({
      twoFactorAuthenticationMethodVerificationStepsOverrideState: TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedSmsTwoFactorAuthenticationMethodVerification,
      allowAbilityToCancel: true,
      handleCancel: () => {
        getTwoFactorAuthenticationDetails();
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
        dispatch(AccountActions.requestUserProfile());
        setShowTwoFactorAuthenticationSetupComponent(false);
        setCustomTwoFactorAuthenticationSetupComponentProps({});
      },
      smsTwoFactorAuthenticationMethodVerifiedPhoneData,
    });
  };

  return (
    <>
      <StyledTwoFactorAuthenticationSection id="account-security-2fa">
        <div className="header-wrapper">
          <i className="fal fa-mobile-alt fa-lg mr-2 dashboard-icon" />
          <h5>Two-factor Authentication</h5>
        </div>

        {
          (
            isLoadingTwoFactorAuthenticationDetails ||
            isValidatingTwoFactorAuthenticationDetails ||
            isLoadingAccountManagementDetails ||
            isLoadingUserProfile
          ) &&
          (
            <div className="loading-container"><Loading /></div>
          )
        }

        {
          (
            errorMsg &&
            !isLoadingTwoFactorAuthenticationDetails &&
            !isValidatingTwoFactorAuthenticationDetails &&
            !isLoadingAccountManagementDetails &&
            !isLoadingUserProfile
          ) &&
          (
            <p className="error">{errorMsg}</p>
          )
        }

        {
          (
            !showTwoFactorAuthenticationSetupComponent &&
            !isLoadingTwoFactorAuthenticationDetails &&
            !isValidatingTwoFactorAuthenticationDetails &&
            !isLoadingAccountManagementDetails &&
            !isLoadingUserProfile &&
            twoFactorAuthenticationDetails &&
            Object.keys(twoFactorAuthenticationDetails).length > 0 &&
            !errorMsg
          ) &&
          (
            <div className="main-content-wrapper">
              <p className="mb-2">You have enabled two-factor authentication.</p>

              <div className="two-fa-methods-row">
                <div className="two-fa-col two-fa-col--sms">
                  <div className="sms-method-section two-fa-method-panel">
                    <div className="method-header-wrapper">
                      <h6>Text method</h6>
                      {twoFactorAuthenticationDetails.smsVerifiedPhoneType ? (
                        <button
                          type="button"
                          className="mute txt-sm"
                          onClick={handleUpdateSmsTwoFactorAuthenticationMethodBtnClick}
                        >
                          (Update)
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="mute txt-sm"
                          onClick={handleVerifySmsTwoFactorAuthenticationMethodBtnClick}
                        >
                          (Verify)
                        </button>
                      )}
                    </div>
                    {twoFactorAuthenticationDetails.smsVerifiedPhoneType && (
                      <p>
                        <span className="verified">
                          <i className="fal fa-check" />
                          Verified
                        </span>
                        <span>
                          {' '}
                          -{' '}
                          {formatPhoneNumber(smsTwoFactorAuthenticationMethodVerifiedPhoneNumber || undefined) ||
                            smsTwoFactorAuthenticationMethodVerifiedPhoneNumber}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="two-fa-col two-fa-col--email">
                  <div className="email-method-section two-fa-method-panel">
                    <div className="method-header-wrapper">
                      <h6>Email method</h6>
                      {!twoFactorAuthenticationDetails.isEmailVerified && (
                        <button
                          type="button"
                          className="mute txt-sm"
                          onClick={handleVerifyEmailTwoFactorAuthenticationMethodBtnClick}
                        >
                          (Verify)
                        </button>
                      )}
                    </div>
                    {twoFactorAuthenticationDetails.isEmailVerified && (
                      <>
                        <p className="txt-sm mute email-change-note">
                          To use a different email for 2FA, update the primary account holder&apos;s email first, then
                          return here to re-verify.
                        </p>
                        <p className="mb-0">
                          <span className="verified">
                            <i className="fal fa-check" />
                            Verified
                          </span>
                          <span> - {primaryAccountHolderEmail}</span>
                        </p>
                        <button
                          type="button"
                          className="btn btn-link email-change-action email-change-action--after-email p-0"
                          onClick={() => dispatch(UIActions.setEmailUpdateModalData({ show: true, accountHolderIndex: 0 }))}
                        >
                          Request primary email change
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {showTwoFactorAuthenticationSetupComponent && (
          <TwoFactorAuthenticationSetup
            primaryBtnClassName="btn btn-primary"
            handleEmailUpdate={() => {
              dispatch(UIActions.setEmailUpdateModalData({ show: true, accountHolderIndex: 0 }));
            }}
            handlePhoneUpdate={() => {
              dispatch(UIActions.setPhoneUpdateModalData({ show: true, accountHolderIndex: 0 }));
            }}
            api={api}
            accountId={accountId}
            handleSetupComplete={() => {
              getTwoFactorAuthenticationDetails();
              dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
              dispatch(AccountActions.requestUserProfile());
              setShowTwoFactorAuthenticationSetupComponent(false);
              setCustomTwoFactorAuthenticationSetupComponentProps({});
            }}
            rootRestHubUrl={Config.tradingApi}
            context={ContextTypes.accountManagementBlock}
            customRequestUserProfile={() => {
              dispatch(AccountActions.requestUserProfile());
            }}
            receiveAccountManagementDetails={(accountManagementDetailsData) => {
              dispatch(AccountManagementDataActions.receiveAccountManagementDetails({ response: accountManagementDetailsData }));
              dispatch(AccountManagementDataActions.requestPendingAccountManagementUpdates({ accountId }));
            }}
            siteGrp={siteGrp}
            {...customTwoFactorAuthenticationSetupComponentProps}
          />
        )}
      </StyledTwoFactorAuthenticationSection>
    </>
  );
};

export default TwoFactorAuthenticationSection;
